import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import type { User, Session } from '@supabase/supabase-js';
import type { AuthConfig, AuthContextType, Profile } from './types';
import { isDevLoginEnabled, performDevLogin, cleanDevLoginUrl } from './dev-login';

const INACTIVITY_CHECK_INTERVAL_MS = 60_000; // check every 60 seconds
const AUTH_OPERATION_TIMEOUT_MS = 15_000;
const AUTH_ERROR_STORAGE_KEY = 'cap4_persistent_auth_error';

function initialPersistentAuthError(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(AUTH_ERROR_STORAGE_KEY);
  } catch {
    return null;
  }
}

function userFacingProfileError(error: unknown): string {
  const detail = error instanceof Error ? error.message : '';
  if (/user not found/i.test(detail)) {
    return 'Din bruger findes ikke i CAP4Dash-dev. Kontakt en administrator.';
  }
  if (/no roles|ingen roller/i.test(detail)) {
    return 'Din bruger har ingen aktive roller i CAP4Dash-dev. Kontakt en administrator.';
  }
  if (/duplicate key|simult/i.test(detail)) {
    return 'Rettighederne blev opdateret samtidigt. Gå tilbage til login og prøv igen.';
  }
  if (/not configured|configuration/i.test(detail)) {
    return 'Rolle- og rettighedssynkroniseringen er ikke konfigureret korrekt.';
  }
  return 'Rolle- og rettighedskontrollen kunne ikke gennemføres. Gå tilbage til login og prøv igen.';
}

function withTimeout<T>(promise: Promise<T>, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => reject(new Error(message)), AUTH_OPERATION_TIMEOUT_MS);
    promise.then(
      (value) => {
        window.clearTimeout(timeoutId);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(timeoutId);
        reject(error);
      },
    );
  });
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const noopLogger = { info: () => {}, warn: () => {}, error: () => {} };

// ─── AuthProvider ───────────────────────────────────────────────────────────

export function AuthProvider({
  config,
  children,
}: {
  config: AuthConfig;
  children: ReactNode;
}) {
  const {
    supabaseClient,
    inactivityTimeoutMs,
    loadProfile: loadProfileFn,
    trackProviderToken = false,
    roleSyncEnabled = false,
    roleSyncFunction,
    logger: log = noopLogger,
  } = config;

  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(initialPersistentAuthError);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [providerToken, setProviderToken] = useState<string | null>(null);

  const lastActivityRef = useRef<number>(Date.now());
  const devLoginPendingRef = useRef(isDevLoginEnabled());
  const devLoginStartedRef = useRef(false);
  const persistentAuthErrorRef = useRef(Boolean(authError));
  const profileLoadRef = useRef<{ userId: string; promise: Promise<void> } | null>(null);
  // Keep stable references to callbacks so effects don't re-run needlessly
  const configRef = useRef(config);
  configRef.current = config;

  const persistAuthError = useCallback((message: string) => {
    persistentAuthErrorRef.current = true;
    setAuthError(message);
    try {
      window.sessionStorage.setItem(AUTH_ERROR_STORAGE_KEY, message);
    } catch {
      // State still keeps the error visible when storage is unavailable.
    }
  }, []);

  const clearAuthError = useCallback(() => {
    persistentAuthErrorRef.current = false;
    setAuthError(null);
    try {
      window.sessionStorage.removeItem(AUTH_ERROR_STORAGE_KEY);
    } catch {
      // Ignore storage failures.
    }
  }, []);

  // ── Activity tracking ───────────────────────────────────────────────────
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  useEffect(() => {
    const events: Array<keyof WindowEventMap> = [
      'mousemove',
      'keydown',
      'mousedown',
      'touchstart',
      'scroll',
    ];
    events.forEach((e) => window.addEventListener(e, updateActivity, { passive: true }));

    // Reset activity timestamp whenever session changes (e.g. sign-in,
    // token refresh) so stale timestamps from the login page or a
    // backgrounded tab never trigger an immediate false-positive timeout.
    if (session) {
      lastActivityRef.current = Date.now();
    }

    const intervalId = setInterval(() => {
      if (session && Date.now() - lastActivityRef.current > inactivityTimeoutMs) {
        log.warn('Session expired due to inactivity', {
          idleMs: Date.now() - lastActivityRef.current,
          timeoutMs: inactivityTimeoutMs,
        });
        supabaseClient.auth.signOut().then(() => {
          configRef.current.onSignOut?.();
        });
      }
    }, INACTIVITY_CHECK_INTERVAL_MS);

    return () => {
      events.forEach((e) => window.removeEventListener(e, updateActivity));
      clearInterval(intervalId);
    };
  }, [session, inactivityTimeoutMs, supabaseClient, updateActivity, log]);

  // ── Profile loader ──────────────────────────────────────────────────────
  const internalLoadProfile = useCallback(
    async (userId: string, currentSession: Session | null) => {
      try {
        if (!loadProfileFn) {
          setLoading(false);
          return;
        }

        const result = await withTimeout(
          loadProfileFn(userId),
          'Profilen kunne ikke hentes inden for tidsgrænsen.',
        );

        if (!result || !result.profile) {
          // App denied access — profile lookup returned null
          log.warn('loadProfile returned no profile — signing out', { userId, result });
          persistAuthError(
            'Din Microsoft-konto blev godkendt, men du har ingen aktiv profil i denne app. Kontakt en administrator.',
          );
          setUser(null);
          setSession(null);
          setProfile(null);
          setUserRoles([]);
          setLoading(false);
          void supabaseClient.auth.signOut().catch((signOutError) => {
            log.error('Failed to clear denied session', signOutError);
          });
          return;
        }

        let resolvedResult = result;

        // Clock authorization depends on Dash. Complete the sync before the
        // authenticated UI and its permission provider are released.
        if (roleSyncEnabled && roleSyncFunction && result.profile.email) {
          const syncResult = await withTimeout(
            roleSyncFunction(userId, result.profile.email),
            'Roller og rettigheder kunne ikke synkroniseres inden for tidsgrænsen.',
          );
          if (!syncResult.synced) {
            throw new Error(syncResult.reason || 'Brugeren har ingen roller i CAP4Dash.');
          }
          log.info('Roles synced externally', { roles: syncResult.roles });
          const refreshed = await withTimeout(
            loadProfileFn(userId),
            'Den synkroniserede profil kunne ikke genindlæses inden for tidsgrænsen.',
          );
          if (refreshed?.profile) resolvedResult = refreshed;
        }

        if (!persistentAuthErrorRef.current) setAuthError(null);
        setProfile(resolvedResult.profile);
        setUserRoles(resolvedResult.roles);

        // Provider token tracking
        if (trackProviderToken && currentSession?.provider_token) {
          setProviderToken(currentSession.provider_token);
        }

      } catch (error) {
        log.error('Error loading profile', error);
        persistAuthError(`Login lykkedes, men adgangen kunne ikke færdiggøres. ${userFacingProfileError(error)}`);
        setUser(null);
        setSession(null);
        setProfile(null);
        setUserRoles([]);
        setProviderToken(null);
        // Clear the unusable session without making the error message wait on
        // another network request.
        void supabaseClient.auth.signOut().catch((signOutError) => {
          log.error('Failed to clear session after profile error', signOutError);
        });
      } finally {
        setLoading(false);
      }
    },
    [supabaseClient, loadProfileFn, trackProviderToken, roleSyncEnabled, roleSyncFunction, log, persistAuthError],
  );

  const loadProfileOnce = useCallback(
    (userId: string, currentSession: Session | null): Promise<void> => {
      if (profileLoadRef.current?.userId === userId) {
        return profileLoadRef.current.promise;
      }
      const promise = internalLoadProfile(userId, currentSession).finally(() => {
        if (profileLoadRef.current?.promise === promise) profileLoadRef.current = null;
      });
      profileLoadRef.current = { userId, promise };
      return promise;
    },
    [internalLoadProfile],
  );

  // ── Dev auto-login (localhost only) ──────────────────────────────────────
  useEffect(() => {
    if (!devLoginPendingRef.current || devLoginStartedRef.current) return;
    devLoginStartedRef.current = true; // Prevent double-fire in StrictMode
    clearAuthError();

    withTimeout(
      performDevLogin(supabaseClient),
      'Automatisk dev-login kunne ikke gennemføres inden for tidsgrænsen.',
    )
      .then(() => {
        log.info('Dev auto-login successful');
        cleanDevLoginUrl();
      })
      .catch((err) => {
        log.error('Dev auto-login failed', err);
        persistAuthError(`Dev login failed: ${err instanceof Error ? err.message : 'unknown error'}`);
        devLoginPendingRef.current = false;
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supabaseClient, clearAuthError, persistAuthError]);

  // ── Auth state listener ─────────────────────────────────────────────────
  useEffect(() => {
    const currentUrl = new URL(window.location.href);
    const hashParams = new URLSearchParams(currentUrl.hash.replace(/^#/, ''));
    const oauthError = currentUrl.searchParams.get('error_description')
      ?? hashParams.get('error_description')
      ?? currentUrl.searchParams.get('error')
      ?? hashParams.get('error');
    if (oauthError) {
      persistAuthError(`Microsoft-login mislykkedes: ${oauthError}`);
      setLoading(false);
    }

    // Initial session check
    withTimeout(
      supabaseClient.auth.getSession(),
      'Login-sessionen kunne ikke indlæses inden for tidsgrænsen.',
    )
      .then(({ data: { session: s }, error }) => {
        if (error) throw error;
        setSession(s);
        setUser(s?.user ?? null);
        if (s?.provider_token && trackProviderToken) {
          setProviderToken(s.provider_token);
        }
        if (s?.user) {
          void loadProfileOnce(s.user.id, s);
        } else if (!devLoginPendingRef.current) {
          // Only show login screen if dev login isn't in progress
          setLoading(false);
        }
      })
      .catch((error) => {
        log.error('Initial auth session check failed', error);
        persistAuthError('Login kunne ikke indlæses. Kontrollér forbindelsen, og prøv igen.');
        setUser(null);
        setSession(null);
        if (!devLoginPendingRef.current) {
          setLoading(false);
        }
      });

    const {
      data: { subscription },
    } = supabaseClient.auth.onAuthStateChange((event, s) => {
      (async () => {
        log.info('Auth state change', { event, hasSession: !!s, hasUser: !!s?.user });

        if (event === 'TOKEN_REFRESHED' && !s) {
          // Token refresh reported no session — try to recover from storage
          // before giving up, since some Supabase JS versions fire this event
          // transiently even when the session is still valid.
          log.warn('TOKEN_REFRESHED with null session — attempting recovery');
          const { data: { session: recoveredSession } } = await supabaseClient.auth.getSession();
          if (recoveredSession?.user) {
            log.info('Session recovered from storage');
            setSession(recoveredSession);
            setUser(recoveredSession.user);
            return;
          }
          log.warn('Session recovery failed — signing out');
          setSession(null);
          setUser(null);
          setProfile(null);
          setUserRoles([]);
          setProviderToken(null);
          setLoading(false);
          return;
        }

        const prevUser = user;
        setSession(s);
        setUser(s?.user ?? null);

        if (s?.provider_token && trackProviderToken) {
          setProviderToken(s.provider_token);
        }

        if (s?.user) {
          devLoginPendingRef.current = false;
          await loadProfileOnce(s.user.id, s);
          // Fire onSignIn only on new sign-in, not on token refresh
          if (!prevUser && s.user && event === 'SIGNED_IN') {
            configRef.current.onSignIn?.();
          }
        } else {
          log.info('Auth state: no user in session', { event, hadPrevUser: !!prevUser });
          setProfile(null);
          setUserRoles([]);
          setProviderToken(null);
          if (!devLoginPendingRef.current) {
            setLoading(false);
          }
          if (prevUser) {
            configRef.current.onSignOut?.();
          }
        }
      })();
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supabaseClient]);

  // ── Actions ─────────────────────────────────────────────────────────────
  const signIn = useMemo(
    () => ({
      withOAuth: async (
        provider: 'azure',
        options?: { scopes?: string; redirectTo?: string },
      ) => {
        clearAuthError();
        const { error } = await supabaseClient.auth.signInWithOAuth({
          provider,
          options: {
            scopes: options?.scopes ?? 'openid profile email',
            redirectTo: options?.redirectTo,
          },
        });
        if (error) throw error;
      },

      withOtp: async (
        email: string,
        options?: { redirectTo?: string; captchaToken?: string },
      ) => {
        clearAuthError();
        const { error } = await supabaseClient.auth.signInWithOtp({
          email,
          options: {
            emailRedirectTo: options?.redirectTo,
            captchaToken: options?.captchaToken,
          },
        });
        if (error) throw error;
      },
    }),
    [supabaseClient, clearAuthError],
  );

  const signOut = useCallback(async () => {
    clearAuthError();
    const { error } = await supabaseClient.auth.signOut();
    if (error) throw error;
    setProfile(null);
    setUserRoles([]);
    setProviderToken(null);
    configRef.current.onSignOut?.();
  }, [supabaseClient, clearAuthError]);

  const refreshProfile = useCallback(async () => {
    if (user) {
      await loadProfileOnce(user.id, session);
    }
  }, [user, session, loadProfileOnce]);

  // ── Derived state ───────────────────────────────────────────────────────
  const isAdmin = userRoles.includes('admin');

  const hasRole = useCallback(
    (role: string) => isAdmin || userRoles.includes(role),
    [isAdmin, userRoles],
  );

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      session,
      profile,
      loading,
      authError,
      clearAuthError,
      userRoles,
      isAdmin,
      hasRole,
      providerToken,
      signIn,
      signOut,
      refreshProfile,
    }),
    [user, session, profile, loading, authError, clearAuthError, userRoles, isAdmin, hasRole, providerToken, signIn, signOut, refreshProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ─── useAuth ────────────────────────────────────────────────────────────────

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
